import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

const CookiePolicy: React.FC = () => {
    return (
        <div className="policy-page">
            <nav className="policy-nav">
                <Link to="/" className="logo">Commerium</Link>
            </nav>

            <main className="policy-content">
                <h1>Cookie Policy</h1>
                <p className="last-updated">Last updated: January 20, 2026</p>

                <section>
                    <h2>1. What Are Cookies?</h2>
                    <p>
                        Cookies are small text files that are placed on your device when you visit a website.
                        They are widely used to make websites work more efficiently, provide a better user experience,
                        and give website owners useful information.
                    </p>
                </section>

                <section>
                    <h2>2. How We Use Cookies</h2>
                    <p>Commerium uses cookies for the following purposes:</p>

                    <h3>2.1 Essential Cookies</h3>
                    <p>
                        These cookies are necessary for the Platform to function properly. They enable core
                        functionality such as:
                    </p>
                    <ul>
                        <li>User authentication and session management</li>
                        <li>Security features and fraud prevention</li>
                        <li>Remembering your preferences</li>
                    </ul>
                    <p><em>These cookies cannot be disabled as they are essential for the Platform to work.</em></p>

                    <h3>2.2 Analytics Cookies</h3>
                    <p>
                        We use privacy-focused analytics (Plausible Analytics) to understand how visitors interact
                        with our Platform. These cookies help us:
                    </p>
                    <ul>
                        <li>Measure website traffic and usage patterns</li>
                        <li>Identify popular pages and features</li>
                        <li>Improve the user experience</li>
                    </ul>
                    <p>Our analytics do not collect personal information and are GDPR-compliant by default.</p>

                    <h3>2.3 Functional Cookies</h3>
                    <p>These cookies enable enhanced functionality and personalization:</p>
                    <ul>
                        <li>Language preferences</li>
                        <li>Theme settings (dark/light mode)</li>
                        <li>Recently viewed items</li>
                    </ul>

                    <h3>2.4 Marketing Cookies</h3>
                    <p>
                        We may use marketing cookies to deliver relevant advertisements and track the effectiveness
                        of our marketing campaigns. These cookies are only set with your consent. We currently do
                        not use third-party advertising cookies.
                    </p>
                </section>

                <section>
                    <h2>3. Cookies We Use</h2>
                    <table className="cookie-table">
                        <thead>
                            <tr>
                                <th>Cookie Name</th>
                                <th>Purpose</th>
                                <th>Duration</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>artisan_token</td>
                                <td>Authentication session</td>
                                <td>7 days</td>
                                <td>Essential</td>
                            </tr>
                            <tr>
                                <td>artisan_user</td>
                                <td>User preferences</td>
                                <td>7 days</td>
                                <td>Essential</td>
                            </tr>
                            <tr>
                                <td>shopify_session_token</td>
                                <td>Shopify integration</td>
                                <td>Session</td>
                                <td>Essential</td>
                            </tr>
                            <tr>
                                <td>cookie_consent</td>
                                <td>Stores your cookie preferences</td>
                                <td>1 year</td>
                                <td>Essential</td>
                            </tr>
                            <tr>
                                <td>_plausible</td>
                                <td>Anonymous analytics</td>
                                <td>Session</td>
                                <td>Analytics</td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                <section>
                    <h2>4. Third-Party Cookies</h2>
                    <p>When you connect third-party services, those services may set their own cookies:</p>
                    <ul>
                        <li><strong>Shopify:</strong> Payment processing cookies</li>
                        <li><strong>Shopify/Etsy/Amazon:</strong> Marketplace integration cookies</li>
                        <li><strong>Google (Gemini API):</strong> AI processing (no cookies stored)</li>
                    </ul>
                    <p>
                        These third-party cookies are governed by the respective privacy policies of those services.
                    </p>
                </section>

                <section>
                    <h2>5. Managing Cookies</h2>
                    <h3>5.1 Browser Settings</h3>
                    <p>
                        You can control and delete cookies through your browser settings. Most browsers allow you to:
                    </p>
                    <ul>
                        <li>View cookies stored on your device</li>
                        <li>Delete individual or all cookies</li>
                        <li>Block cookies from specific or all websites</li>
                        <li>Set preferences for first-party vs. third-party cookies</li>
                    </ul>
                    <p>
                        Note: Blocking essential cookies may prevent the Platform from functioning correctly.
                    </p>

                    <h3>5.2 Cookie Consent</h3>
                    <p>
                        When you first visit our Platform, you will see a cookie consent banner. You can:
                    </p>
                    <ul>
                        <li>Accept all cookies</li>
                        <li>Accept only essential cookies</li>
                        <li>Customize your preferences</li>
                    </ul>
                    <p>
                        You can change your cookie preferences at any time by clicking "Cookie Settings" in the footer.
                    </p>
                </section>

                <section>
                    <h2>6. Do Not Track</h2>
                    <p>
                        Some browsers have a "Do Not Track" feature. We honor Do Not Track signals and do not
                        track, plant cookies, or use advertising when a DNT browser mechanism is in place.
                    </p>
                </section>

                <section>
                    <h2>7. Changes to This Policy</h2>
                    <p>
                        We may update this Cookie Policy from time to time. Changes will be posted on this page
                        with an updated "Last updated" date. We encourage you to review this policy periodically.
                    </p>
                </section>

                <section>
                    <h2>8. Contact Us</h2>
                    <p>If you have questions about our use of cookies, contact us at:</p>
                    <ul>
                        <li>Email: privacy@trycommerium.com</li>
                        <li>Address: Commerium Technologies, Calle Mayor 1, 28001 Madrid, Spain</li>
                    </ul>
                </section>
            </main>

            <footer className="policy-footer">
                <Link to="/">← Back to Home</Link>
                <p>© 2026 Commerium. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default CookiePolicy;
